import { useCallback, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import cx from 'classnames';
import { UPLOAD_SOURCES, getLogger } from '@cld/upload-widget-common';
import t from 'common/i18n';
import navItems from 'root/routes/navItems';
import { selectMenuData, selectConfigLanguage, selectTopMenuConfigTitles, selectIsInlineMode } from '../../selectors';
import bindActions, { types as ats } from '../../actions';
import { getComponentConsumer } from '../UploadContainer/uploaderContext';
import TopMenuItem from '../TopMenuItem/TopMenuItem';
import ScrollableMenuItems from './items/ScrollableMenuItems';
import ResponsiveMenuItems from './items/ResponsiveMenuItems';
import { sendAnalyticsTabNavigationEvent } from '../../../common/analytics/analytics.helpers';

import './TopMenu.scss';

const logger = getLogger();

const MENU_CLS = {
  [UPLOAD_SOURCES.LOCAL]: 'files',
  [UPLOAD_SOURCES.DROPBOX]: 'dropbox',
  [UPLOAD_SOURCES.CAMERA]: 'camera',
  [UPLOAD_SOURCES.URL]: 'web-addr',
  [UPLOAD_SOURCES.IMAGE_SEARCH]: 'img-search',
  [UPLOAD_SOURCES.FACEBOOK]: 'facebook',
  [UPLOAD_SOURCES.GOOGLE_DRIVE]: 'google_drive',
  [UPLOAD_SOURCES.INSTAGRAM]: 'instagram',
  [UPLOAD_SOURCES.ISTOCK]: 'istock',
  [UPLOAD_SOURCES.GETTY]: 'getty',
  [UPLOAD_SOURCES.UNSPLASH]: 'unsplash',
};

const SingleTopMenu = styled(TopMenuItem)`
  margin-left: 10px;
`;

const getActiveItem = (currentSource) => navItems.find((i) => i.id === currentSource);

export const TopMenu = ({
  enabledSources,
  currentSource,
  responsiveShow,
  isSingle,
  isResponsiveWidth,
  configTitles,
  lang,
  isInline,
  history,
  location,
  actions,
  uploader,
}) => {
  const menuItems = useMemo(() => enabledSources.map((src) => navItems.find((item) => item.id === src)).filter(Boolean), [enabledSources]);

  const itemsTitles = useMemo(() => {
    logger.log(`[TopMenu]: creating titles for top menu items. lang = ${lang}`, configTitles);

    return enabledSources.reduce((res, source) => {
      const item = navItems.find((item) => item.id === source);
      res[source] = t(item.title);
      return res;
    }, {});
  }, [enabledSources, configTitles, lang]);

  const navigateToPath = useCallback(
    (path) => {
      const src = path.replace('/', '');
      sendAnalyticsTabNavigationEvent(src);
      history.push({
        ...location,
        pathname: path,
      });
    },
    [history, location]
  );

  useEffect(() => {
    // if the source being shown is no longer enabled
    if (currentSource && !~enabledSources.indexOf(currentSource)) {
      logger.log(`[TopMenu]: current source (${currentSource}) has been removed with config update. need to navigate to another source`);

      const firstFound = navItems.find((i) => i.id === enabledSources[0]);

      if (firstFound) {
        navigateToPath(firstFound.path);
      }
    }
  }, [enabledSources, currentSource, navigateToPath]);

  const activeItem = getActiveItem(currentSource);

  const activeItemId = activeItem ? activeItem.id : '';

  const menuClass = activeItemId ? MENU_CLS[activeItemId] : '';

  const navClasses = cx('menu', {
    //create space between close button and scrollable forward button
    pr8: !isInline && !isResponsiveWidth,
  });

  const showFileSelection = useCallback(() => {
    uploader.uploadFieldClick();
  }, [uploader]);

  const renderSingleItem = () => (activeItem ? <SingleTopMenu {...activeItem} title={t(activeItem.title)} isSingle /> : null);

  const renderMenuItems = () => {
    const activeItem = getActiveItem(currentSource);
    const activeItemId = activeItem ? activeItem.id : '';

    return isResponsiveWidth ? (
      <ResponsiveMenuItems
        items={menuItems}
        titles={itemsTitles}
        activeItemId={activeItemId}
        navigateToPath={navigateToPath}
        isMenuOpen={responsiveShow}
        showFileSelection={showFileSelection}
        toggleMenu={actions[ats.TOGGLE_RESPONSIVE_MENU]}
      />
    ) : (
      <ScrollableMenuItems items={menuItems} titles={itemsTitles} activeItemId={activeItemId} navigateToPath={navigateToPath} />
    );
  };

  return (
    <header
      id="cloudinary-navbar"
      data-test="uw-top-menu-header"
      className={cx('head', 'header', 'bg-window', 'bg-window-fc', 'bc-window-border', {
        'thin-menu': isSingle || (isResponsiveWidth && !responsiveShow),
        [`${menuClass}-active ${activeItemId}-active`]: activeItem,
      })}
    >
      <nav className={navClasses} data-test="uw-top-menu">
        {isSingle && !isResponsiveWidth ? renderSingleItem() : renderMenuItems()}
      </nav>
    </header>
  );
};

const mapStateToProps = (state) => {
  return {
    ...selectMenuData(state),
    lang: selectConfigLanguage(state), // needed to cause re-render when config changes
    configTitles: selectTopMenuConfigTitles(state), // needed to cause re-render when config changes
    isInline: selectIsInlineMode(state),
  };
};

export default connect(mapStateToProps, (dispatch) => ({
  actions: bindActions(dispatch),
}))(getComponentConsumer(withRouter(TopMenu)));
